import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { AVAILABLE_ICONS, ICON_DATA, IconName } from './icon-data.generated';

type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'custom';
export type IconColor =
  | 'primary'
  | 'on-primary'
  | 'secondary'
  | 'on-secondary'
  | 'warn'
  | 'on-surface'
  | 'outline-variant'
  | 'custom'
  | 'green'
  | 'on-primary-container'
  | 'on-surface-variant';

@Component({
  selector: 'app-icon',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './icon.component.html',
  styleUrl: './icon.component.scss',
})
export class IconComponent implements OnInit, OnChanges {
  private sanitizer = inject(DomSanitizer);

  @Input({ required: true }) name!: IconName;
  @Input() size: IconSize = 'md';
  @Input() customSize?: string;
  @Input() color: IconColor = 'primary';
  @Input() customColor?: string; // e.g., '#ff9800' or 'rebeccapurple' or 'var(--my-color)'
  @Input() ariaLabel?: string | null;
  @Input() role = 'img';

  svgContent: SafeHtml = '';

  ngOnInit(): void {
    this.updateIconSvg();

    // gentle nudge if custom requested but no value provided
    if (this.color === 'custom' && !this.customColor) {
      console.warn('AppIcon: color is "custom" but no customColor provided.');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['name']) {
      this.updateIconSvg();
    }
  }

  private updateIconSvg(): void {
    const iconSvg = ICON_DATA[this.name];
    if (!iconSvg) {
      console.error(`Icon '${this.name}' not found. Available icons:`, AVAILABLE_ICONS);
      return;
    }

    // ensure inline SVG is themable and sized by wrapper
    const themed = iconSvg
      .replace(/fill="[^"]*"/g, 'fill="currentColor"')
      .replace(/width="[^"]*"/g, '')
      .replace(/height="[^"]*"/g, '');

    // The Material Symbols exports are inconsistent: most declare fill="#1f1f1f"
    // on the root <svg> (rewritten above), but some declare no fill at all and
    // fall back to the SVG default — black, which ignores `color` entirely and
    // all but vanishes in dark mode. The `.app-icon svg { fill: currentColor }`
    // rule in this component's stylesheet cannot cover for that: this markup is
    // injected with innerHTML, so it never receives the encapsulation attribute
    // that rule is scoped to. Declaring the fill in the markup is what actually
    // reaches the icon. Paths inherit `fill`, so the root is enough.
    const normalized = themed.replace(/<svg\b([^>]*)>/, (tag, attrs: string) =>
      /\sfill=/.test(attrs) ? tag : `<svg fill="currentColor"${attrs}>`,
    );

    this.svgContent = this.sanitizer.bypassSecurityTrustHtml(normalized);
  }

  get sizeClass(): string {
    return this.size === 'custom' ? '' : `size-${this.size}`;
  }

  get colorClass(): string | null {
    return this.color === 'custom' ? null : `color-${this.color}`;
  }

  get inlineColor(): string | null {
    // only apply inline color when custom
    return this.color === 'custom' ? (this.customColor ?? null) : null;
  }
}
